import { useEffect, useState } from 'react';
import { Typography } from '../typography';
import { Button } from '../button';
import { Input, InputNumber, InputPrice } from '../input';
import { BorderLine } from '../border-line';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { enqueueSnackbar } from '../snackbar';
import { useStore } from '../../context';
import {
  useConvertCurrencyFcQuery,
  useStoragePricingForm,
  useUpdatePricingMutation,
} from '../../hooks';
import { formatContainerType, momentTextFormat } from '../../utils';
import { saveCache, getCache } from '../../services';
import {
  containerTypes,
  CURRENCIES,
  currencyOptions,
  INPUT_ERRORS,
  MESSAGES,
  Pricing,
  pricingDetails,
  pricingFormLabels,
  STORAGE_PRICING,
  StoragePricing,
} from '../../const';
import { ContainerType, StoragePricingUpdateModalCache } from '../../types';
import { Select } from '../select';
import { i18n } from '../../i18n';

export const StoragePricingUpdateModal = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const { mutate } = useUpdatePricingMutation();
  const convertCurrency = useConvertCurrencyFcQuery();

  const [showPricingFormModal, setShowPricingFormModal] = useState(false);
  const [yardName, setYardName] = useState('');
  const [clientName, setClientName] = useState('');
  const [{ effectiveDate, freeDays, currencyCode, ...containers }, setPricingState] =
    useStoragePricingForm({
      effectiveDate: '',
    });
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [updateKey, setUpdateKey] = useState(Math.random());

  const validators = {
    freeDays: String(freeDays).length,
    ...['loaded', 'empty', 'loadedCraneIn', 'loadedCraneOut', 'emptyCraneIn', 'emptyCraneOut'].map(
      (key) => ({
        [key]: containerTypes
          .map((containerType) => containers[containerType.value][key as keyof Pricing])
          .every((val: number) => String(val).length !== 0),
      }),
    ),
  };

  useEffect(() => {
    const open = () => {
      const cachedData = getCache<StoragePricingUpdateModalCache>('storage-pricing-update-modal');

      const { containers, ...pricing } = cachedData.activePricing;
      setYardName(cachedData.clientYard?.yard.name);
      setClientName(cachedData.clientYard?.client.code);
      setPricingState({
        ...STORAGE_PRICING,
        effectiveDate: momentTextFormat(pricing.activationDate, user),
        freeDays: pricing.freeDays,
        currencyCode: pricing.currencyCode,
        ...containers.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.type]: {
              loaded: curr.loaded,
              empty: curr.empty,
              loadedCraneIn: curr.loadedCraneIn,
              loadedCraneOut: curr.loadedCraneOut,
              emptyCraneIn: curr.emptyCraneIn,
              emptyCraneOut: curr.emptyCraneOut,
            },
          }),
          {},
        ),
      } as StoragePricing);

      setIsSubmitClicked(false);
      setShowPricingFormModal(true);
    };

    saveCache<StoragePricingUpdateModalCache>('storage-pricing-update-modal', (props) => ({
      ...props,
      open,
    }));
  }, []);

  const handleOnClose = () => {
    const openModalFc = getCache<StoragePricingUpdateModalCache>(
      'storage-pricing-update-modal',
    ).open;
    saveCache('storage-pricing-update-modal', { open: openModalFc });
    setShowPricingFormModal(!open);
  };

  const handleCurrencyChange = (e: any) => {
    const oldCurrencyCode = currencyCode;
    const newCurrencyCode = e.target.value;

    setPricingState((props) => {
      props.currencyCode = e.target.value;

      (Object.keys(containers) as Array<keyof typeof containers>).forEach((containerType) => {
        pricingDetails.forEach((detail) => {
          const value = props[containerType][detail];
          const convertedValue = convertCurrency(oldCurrencyCode, newCurrencyCode, value);
          props[containerType][detail] = Number(convertedValue.toFixed(2));
        });
      });

      setUpdateKey(Math.random());
    });
  };

  const handleSubmitClick = async () => {
    setIsSubmitClicked(true);
    if ([...Object.values(validators)].some((val) => !val)) {
      return;
    }

    const formProps = {
      id: getCache<StoragePricingUpdateModalCache>('storage-pricing-update-modal').id,
      activePricingId: getCache<StoragePricingUpdateModalCache>('storage-pricing-update-modal')
        .activePricing.id,
      freeDays,
      currencyCode,
      ...containers,
    };

    mutate(formProps, {
      onSuccess: () => {
        enqueueSnackbar(MESSAGES.STORAGE_PRICING.SUCCESS.UPDATED, 'success');
        setShowPricingFormModal(false);
        getCache<StoragePricingUpdateModalCache>('storage-pricing-update-modal').refetch();
      },
    });
  };

  return (
    <ModalBase
      id={'storage-pricing-update-modal'}
      open={showPricingFormModal}
      onClose={handleOnClose}
      paperSize={'very-big'}
    >
      <ModalWrapper>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.modal.storagePricingUpdateTitle')}
        </Typography>
        {showPricingFormModal && (
          <>
            <ModalBodyCustomColumns columns={'1fr 1fr 1fr 1fr'}>
              <Input
                label={i18n.t('common.client')}
                data-size={'medium'}
                defaultValue={clientName}
                readOnly
              />
              <Input
                label={i18n.t('common.yard')}
                data-size={'medium'}
                defaultValue={yardName}
                readOnly
              />
              <Input
                label={i18n.t('label.effectiveDate')}
                data-size={'medium'}
                defaultValue={effectiveDate}
                readOnly
              />
              <InputNumber
                label={i18n.t('label.freeDays')}
                data-size={'medium'}
                defaultValue={freeDays}
                onChange={(e) =>
                  setPricingState((props) => {
                    props.freeDays = Number(e.target.value);
                  })
                }
                error={isSubmitClicked ? !String(freeDays).length : false}
                helperText={
                  isSubmitClicked && !String(freeDays).length ? INPUT_ERRORS.REQUIRED.FREE_DAYS : ''
                }
              />
              <Select
                label={i18n.t('label.currencyCode')}
                options={currencyOptions}
                value={currencyCode}
                onChange={handleCurrencyChange}
              />
            </ModalBodyCustomColumns>
            <BorderLine space={'dashboard-form'} />
            {Object.keys(containers).map((containerType, i) => (
              <ModalBodyCustomColumns
                key={`container-${i}-${updateKey}`}
                columns={'1fr 1fr 1fr 1fr 1fr 1fr'}
              >
                {pricingDetails.map((detail, n) => (
                  <InputPrice
                    key={`detail-${n}`}
                    label={pricingFormLabels[detail](
                      formatContainerType(containerType as ContainerType),
                    )}
                    data-size={'medium'}
                    defaultValue={containers[containerType as ContainerType][detail]}
                    onChange={(e) => {
                      setPricingState((props) => {
                        props[containerType as ContainerType][detail] = Number(e.target.value);
                      });
                    }}
                    endIcon={CURRENCIES[currencyCode].symbol}
                    error={
                      isSubmitClicked
                        ? !String(containers[containerType as ContainerType]).length
                        : false
                    }
                    helperText={
                      isSubmitClicked
                        ? !String(containers[containerType as ContainerType]).length
                          ? INPUT_ERRORS.REQUIRED.PRICE
                          : ''
                        : ''
                    }
                  />
                ))}
              </ModalBodyCustomColumns>
            ))}
          </>
        )}
        <ModalFooterButtons>
          <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
            {i18n.t('button.cancel')}
          </Button>
          <Button size={'medium'} data-color={'primary'} onClick={handleSubmitClick}>
            {i18n.t('button.update')}
          </Button>
        </ModalFooterButtons>
      </ModalWrapper>
    </ModalBase>
  );
};
